import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useShop } from "../../contexts/ShopContext";
import { fetchMetaReportSummary } from "../../utils/api/reportApi";
import pageStyles from "../../styles/PageLayout.module.css";
import reportDetailStyles from "../ReportDetailPage/ReportDetailPage.module.css";
import styles from "./MetaReportOpenPage.module.css";

const metricGroups = [
  {
    title: "Spend & Returns",
    items: ["Spend", "Revenue", "ROAS", "CPC", "CPM"],
  },
  {
    title: "Reach & Engagement",
    items: ["Impressions", "Clicks", "CTR", "Bounce Rate", "Engagements"],
  },
  {
    title: "Funnel",
    items: ["Add To Cart", "Init. Checkout", "Purchases", "AOV"],
  },
  {
    title: "Customer Acquisition",
    items: [
      "New Customers",
      "Repeat Customers",
      "CAC",
      "30D CLTV",
      "60D CLTV",
      "90D CLTV",
      "CLTV : CAC",
    ],
  },
];

const MetaReportOpenPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthUser();
  const { selectedShop, shopData } = useShop();

  const authToken = user?.token;
  const userId = user?._id || user?.id || user?.userId;
  const shop = selectedShop || shopData || null;
  const trace = location.state?.reportOpenTrace || null;
  const [traceId] = useState(
    () => trace?.traceId || `report-open:meta:${Date.now()}:route`,
  );
  const [status, setStatus] = useState("loading");

  const isMongoId = (value) => /^[a-f0-9]{24}$/i.test(String(value || ""));
  const shopId = isMongoId(shop?._id)
    ? shop._id
    : isMongoId(shop?.id)
      ? shop.id
      : null;

  const RETRY_DELAYS_MS = [0, 700, 1400, 2200];

  const attemptsRef = useRef(0);

  useEffect(() => {
    let active = true;
    // Wait until all three auth/shop values are ready before fetching
    if (!authToken || !userId || !shopId) return undefined;

    attemptsRef.current = 0;

    console.log("[report-open][frontend] meta-route.mount", {
      traceId,
      userId,
      shopId,
    });

    const attempt = async () => {
      const attemptIndex = attemptsRef.current;
      console.log("[report-open][frontend] meta-route.attempt", {
        traceId,
        attempt: attemptIndex + 1,
        maxAttempts: RETRY_DELAYS_MS.length,
      });

      try {
        const response = await fetchMetaReportSummary({
          userId,
          shopId,
          authToken,
          traceId,
        });
        if (!active) return;

        const report = response?.report || null;
        const reportId = report?._id || report?.id || null;

        console.log("[report-open][frontend] meta-route.fetch_complete", {
          traceId,
          attempt: attemptIndex + 1,
          found: Boolean(reportId),
          reportId: reportId || null,
        });

        if (reportId) {
          navigate(`/reports/${reportId}`, {
            replace: true,
            state: {
              prefetchedReport: report,
              reportOpenTrace: {
                traceId,
                source: "meta-route",
                clickStartedAtMs: trace?.clickStartedAtMs || Date.now(),
              },
            },
          });
          return;
        }

        // No report yet — retry if attempts remain
        attemptsRef.current += 1;
        if (attemptsRef.current < RETRY_DELAYS_MS.length) {
          const delay = RETRY_DELAYS_MS[attemptsRef.current];
          setStatus("loading");
          setTimeout(() => { if (active) attempt(); }, delay);
        } else {
          console.log("[report-open][frontend] meta-route.retries_exhausted", { traceId });
          setStatus("pending");
        }
      } catch (error) {
        if (!active) return;
        console.log("[report-open][frontend] meta-route.fetch_error", {
          traceId,
          attempt: attemptIndex + 1,
          error: error?.message || String(error),
        });

        attemptsRef.current += 1;
        if (attemptsRef.current < RETRY_DELAYS_MS.length) {
          const delay = RETRY_DELAYS_MS[attemptsRef.current];
          setTimeout(() => { if (active) attempt(); }, delay);
        } else {
          console.log("[report-open][frontend] meta-route.retries_exhausted", { traceId });
          setStatus("error");
        }
      }
    };

    attempt();

    return () => {
      active = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, shopId, userId]);

  return (
    <section
      className={`${pageStyles.page} ${reportDetailStyles.wrapper}`}
      data-page="report-detail"
    >
      <div className={reportDetailStyles.splitShell}>
        <div className={reportDetailStyles.viewerPane}>
          <div className={reportDetailStyles.viewerCard}>
            <div className={reportDetailStyles.viewerHeader}>
              <section className={styles.hero}>
                <div className={styles.titleBlock}>
                  <div className={`${styles.bar} ${styles.titleBar}`} />
                </div>

                <div className={styles.tabRow}>
                  <div className={`${styles.pill} ${styles.pillActive}`} />
                  <div className={styles.pill} />
                </div>
              </section>
            </div>

            <div className={reportDetailStyles.viewerScroll}>
              <section className={styles.panel}>
                <div className={styles.controlsRow}>
                  <div className={styles.controlPill} />
                  <div
                    className={`${styles.controlPill} ${styles.controlPillWide}`}
                  />
                </div>

                <div className={styles.banner}>
                  <div className={`${styles.bar} ${styles.bannerLabel}`} />
                  <div className={styles.bannerBadge} />
                  {status === "pending" ? (
                    <span style={{ fontSize: "0.85rem", opacity: 0.6 }}>
                      Preparing your Meta report&hellip;
                    </span>
                  ) : status === "error" ? (
                    <span style={{ fontSize: "0.85rem", opacity: 0.6 }}>
                      Could not load report. Please try again.
                    </span>
                  ) : (
                    <div className={`${styles.bar} ${styles.bannerText}`} />
                  )}
                </div>

                {metricGroups.map((group) => (
                  <div key={group.title} className={styles.group}>
                    <div className={`${styles.bar} ${styles.groupTitle}`} />
                    <div className={styles.metricGrid}>
                      {group.items.map((item, index) => (
                        <div
                          key={`${group.title}-${item}-${index}`}
                          className={styles.metricCard}
                        >
                          <div className={`${styles.bar} ${styles.metricLabel}`} />
                          <div className={`${styles.bar} ${styles.metricValue}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MetaReportOpenPage;
